import React, { useState } from 'react';
import { useQuery, useQueryClient } from 'react-query';
import { CalendarDaysIcon } from '@heroicons/react/24/solid';
import { CalendarDaysIcon as CalendarDaysIconOutline } from '@heroicons/react/24/outline';
import { toast } from 'react-toastify';
import api from '../services/api';
import useAuthStore from '../store/authStore';

const VacationStatusBar = () => {
  const queryClient = useQueryClient();
  const [showVacationModal, setShowVacationModal] = useState(false);
  const [vacationForm, setVacationForm] = useState({
    start_date: '',
    end_date: '',
    reason: ''
  });
  
  const { data: vacationStatus, refetch: refetchVacationStatus } = useQuery(
    ['vacation-status'],
    () => api.get('/users/vacation/status').then(res => res.data),
    {
      refetchInterval: 30000, // 30초마다 새로고침
      retry: false,
      refetchOnWindowFocus: true, // 창 포커스 시 자동 새로고침
      staleTime: 10000 // 10초 후 데이터를 stale로 표시
    }
  );

  const handleVacationEnd = async () => {
    try {
      const today = new Date().toISOString().split('T')[0];
      await api.post('/users/vacation', {
        start_date: today,
        end_date: today,
        reason: '휴가 종료'
      });
      toast.success('휴가가 종료되었습니다.');
      setShowVacationModal(false);
      setVacationForm({ start_date: '', end_date: '', reason: '' });
      await queryClient.invalidateQueries(['vacation-status']);
      await refetchVacationStatus();
    } catch (error) {
      toast.error('휴가 종료에 실패했습니다.');
    }
  };

  // 본인이 휴가 중인 경우만 표시
  if (!vacationStatus?.is_on_vacation) return null;

  return (
    <>
      <div className="bg-blue-500 text-white">
        <div className="px-4 sm:px-6 lg:px-8 py-3">
          <div className="flex items-center justify-between max-w-7xl mx-auto">
            <div className="flex items-center gap-2">
              <CalendarDaysIcon className="h-5 w-5 flex-shrink-0" />
              <span className="text-sm font-medium">
                휴가 중입니다 ({vacationStatus.days_remaining}일 남음)
              </span>
            </div>
            <button
              onClick={() => setShowVacationModal(true)}
              className="text-xs underline hover:no-underline whitespace-nowrap"
            >
              설정 변경
            </button>
          </div>
        </div>
      </div>

      {/* 휴가 설정 모달 */}
      {showVacationModal && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-end sm:items-center justify-center sm:p-4">
          <div className="bg-white w-full sm:max-w-md rounded-t-3xl sm:rounded-3xl shadow-2xl">
            <div className="p-6">
              <div className="relative">
                <div className="absolute inset-0 bg-blue-500 rounded-full blur-2xl opacity-20"></div>
                <div className="relative w-16 h-16 bg-gradient-to-br from-blue-400 to-blue-600 rounded-full flex items-center justify-center mx-auto mb-4 shadow-lg">
                  <CalendarDaysIconOutline className="h-8 w-8 text-white" />
                </div>
              </div>
              
              <h3 className="text-lg font-semibold text-gray-900 text-center mb-2">
                휴가 설정
              </h3>
              
              <div className="mb-4">
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-4">
                  <p className="text-sm font-medium text-blue-800 mb-1">
                    🏖️ 현재 휴가 중입니다
                  </p>
                  <p className="text-xs text-blue-600">
                    남은 기간: <span className="font-bold">{vacationStatus.days_remaining}일</span>
                  </p>
                  {vacationStatus.reason && (
                    <p className="text-xs text-blue-600 mt-1">
                      사유: {vacationStatus.reason}
                    </p>
                  )}
                </div>
                <button
                  onClick={() => {
                    if (window.confirm('휴가를 종료하시겠습니까?')) {
                      handleVacationEnd();
                    }
                  }}
                  className="w-full px-4 py-3 bg-blue-600 text-white rounded-xl font-medium hover:bg-blue-700 transition-colors"
                >
                  지금 업무 복귀 (휴가 종료)
                </button>
              </div>
              
              <p className="text-xs text-gray-500 text-center mt-4">
                * 휴가 중에는 프로젝트 배정이 제한됩니다.
              </p>
            </div>
            
            <div className="flex gap-3 p-4 bg-gray-50 rounded-b-3xl">
              <button
                onClick={() => setShowVacationModal(false)}
                className="flex-1 px-4 py-3 bg-white text-gray-700 rounded-xl font-medium hover:bg-gray-100 transition-colors shadow-sm"
              >
                닫기
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default VacationStatusBar;